import { Badge } from '@mdvip/shared/components/ui/badge';
import { cn } from '@mdvip/shared/lib/utils';
import { Link } from 'react-router-dom';

import { type RecipeTaxonomyField, recipeTagHref } from './recipeDetail.util';

export interface RecipeTag {
  field: RecipeTaxonomyField;
  value: string;
}

export interface RecipeTagsProps {
  /** The nine taxonomy arrays, already flattened by the caller. Deduplicated again here. */
  tags: RecipeTag[];
  className?: string;
}

/**
 * The recipe's taxonomy values as a row of pills, each linking into the Recipes listing
 * pre-filtered to that field's value. Every value is clickable: the listing renders all nine
 * taxonomy fields as filter groups, so there is no such thing as a value with nowhere to go.
 *
 * A value appearing under more than one field is shown once, keeping the first field's link.
 */
export function RecipeTags({ tags, className }: RecipeTagsProps) {
  const seen = new Set<string>();
  const unique = tags.filter((tag) => {
    if (seen.has(tag.value)) return false;
    seen.add(tag.value);
    return true;
  });

  if (unique.length === 0) return null;

  return (
    <div data-slot="recipe-tags" className={cn('flex flex-wrap gap-2', className)}>
      {unique.map((tag) => (
        <Badge
          key={tag.value}
          variant="secondary"
          className="cursor-pointer font-medium transition-colors hover:bg-accent"
          render={<Link to={recipeTagHref(tag.field, tag.value)} />}
        >
          {tag.value}
        </Badge>
      ))}
    </div>
  );
}
